import type { SendOutcome } from '../../lib/rcu/link/session';
import type { RcuSceneEntry } from '../../lib/rcu/wire';
import { KEYPAD_LIMITS } from './catalog/limits';
import { encodeKeypad, triggerOf, triggersOf } from './encode';
import type { Keypad } from './model';
import type { MappingTarget } from './targets';

/**
 * How the feature reaches the device.
 *
 * The feature knows nothing about brokers, topics or the link's own state: the
 * console hands it a port, and this is the whole of what the feature may ask of
 * it. That boundary is what keeps the keypad book workable when the tablet is
 * offline — nothing here is required to display or edit the panels — and it is
 * why the feature can be tested without a transport at all.
 */
export interface KeypadPort {
  /** Sends one document of scene entries. One call carries one panel. */
  readonly sendMapping: (entries: readonly RcuSceneEntry[]) => Promise<SendOutcome>;
  /** Asks the device for one trigger's stored mapping. */
  readonly readMapping: (trigger: readonly number[]) => Promise<SendOutcome>;
  /** Erases the mappings at these triggers. */
  readonly eraseMappings: (triggers: readonly (readonly number[])[]) => Promise<SendOutcome>;
  /** Waits between reads, so the firmware's queue can keep up. */
  readonly pause: (milliseconds: number) => Promise<void>;
}

/** One state as it was sent, so the caller can remember what was delivered. */
export interface WrittenState {
  readonly triggerKey: string;
  readonly fingerprint: string;
}

/** What became of one panel's write. */
export interface WriteOutcome {
  /** True when the transport reported that it delivered the document. */
  readonly delivered: boolean;
  /** What the transport said, in its own words. */
  readonly detail: string;
  /**
   * The states the document covered. Delivered is not confirmed: the firmware
   * answers `rcuaddmapping` with an acknowledgement that carries nothing to match
   * it to a request, so these are remembered as sent, not as stored.
   */
  readonly written: readonly WrittenState[];
}

/** What became of one panel's read. */
export interface ReadOutcome {
  /** The triggers that were asked for, in the order they were asked. */
  readonly asked: readonly string[];
  readonly delivered: boolean;
  readonly detail: string;
}

const NOTHING_TO_WRITE = 'Nothing to write: no key state of this panel drives an output.';

/** Sends everything a panel's key states drive, as one document. */
export const writePanel = async (input: {
  readonly keypad: Keypad;
  readonly targets: readonly MappingTarget[];
  readonly port: KeypadPort;
}): Promise<WriteOutcome> => {
  const encoded = encodeKeypad(input.keypad, input.targets);
  if (encoded.entries.length === 0) {
    return { delivered: false, detail: NOTHING_TO_WRITE, written: [] };
  }

  const outcome = await input.port.sendMapping(encoded.entries.map((entry) => entry.entry));
  return {
    delivered: outcome.ok,
    detail: outcome.detail,
    written: encoded.entries.map((entry) => ({
      triggerKey: entry.triggerKey,
      fingerprint: entry.fingerprint,
    })),
  };
};

/**
 * Asks the device what it holds for every key state of a panel.
 *
 * One read per trigger, paced: the firmware queues twenty messages and then
 * drops, and every mapping read is answered with a publish that the read buffer
 * can truncate — which is why a trigger that answers nothing is reported as
 * "nothing stored" rather than "no such mapping".
 */
export const readPanel = async (input: {
  readonly keypad: Keypad;
  readonly port: KeypadPort;
  /** The address to ask about; the panel's own when omitted. */
  readonly address?: number | null;
}): Promise<ReadOutcome> => {
  const refs = triggersOf(input.keypad, input.address);
  const asked: string[] = [];
  let delivered = true;
  let detail = '';

  for (const ref of refs) {
    if (asked.length > 0) await input.port.pause(KEYPAD_LIMITS.READ_SPACING_MS);
    const outcome = await input.port.readMapping(triggerOf(ref.selector, ref.state));
    asked.push(ref.triggerKey);
    if (!outcome.ok) {
      delivered = false;
      detail = outcome.detail;
    }
  }

  return { asked, delivered, detail };
};

/**
 * Erases what the device holds at these triggers, in one document.
 *
 * One document rather than one per trigger: a panel has at most twelve triggers,
 * and the firmware's queue takes twenty before it starts dropping.
 */
export const eraseTriggers = async (input: {
  readonly triggers: readonly (readonly number[])[];
  readonly port: KeypadPort;
}): Promise<SendOutcome> => input.port.eraseMappings(input.triggers);
